import {
  GA4_CONSENT_ANALYTICS_ONLY,
  GA4_CONSENT_DENIED,
  buildSanitizedHitParams,
  gaCookieNamesFromDocumentCookie,
  gaDisableProperty,
  parseGa4MeasurementId,
} from './ga4'

export const GTAG_SCRIPT_ID = 'ga4-gtag'

let activeMeasurementId: string | null = null
let gtagConfigured = false

type GtagFn = (...args: unknown[]) => void

function dataLayer(): unknown[] {
  const w = window as Window & { dataLayer?: unknown[] }
  w.dataLayer = w.dataLayer || []
  return w.dataLayer
}

export function gtag(...args: unknown[]): void {
  dataLayer().push(args)
}

export function setActiveGa4MeasurementId(id: string | null): void {
  activeMeasurementId = parseGa4MeasurementId(id)
}

export function getActiveGa4MeasurementId(): string | null {
  return activeMeasurementId
}

export function setGa4Disabled(measurementId: string, disabled: boolean): void {
  const id = parseGa4MeasurementId(measurementId)
  if (!id) return
  ;(window as unknown as Record<string, unknown>)[gaDisableProperty(id)] = disabled
}

function expireCookie(name: string): void {
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT'
  const encodings = [
    `${name}=; expires=${expires}; path=/`,
    `${name}=; expires=${expires}; path=/; domain=${window.location.hostname}`,
  ]
  const hostParts = window.location.hostname.split('.')
  if (hostParts.length >= 2) {
    encodings.push(`${name}=; expires=${expires}; path=/; domain=.${hostParts.slice(-2).join('.')}`)
  }
  for (const cookie of encodings) document.cookie = cookie
}

export function clearGa4Cookies(cookie = document.cookie): string[] {
  const names = gaCookieNamesFromDocumentCookie(cookie)
  for (const name of names) expireCookie(name)
  return names
}

export function denyGa4Consent(): void {
  gtag('consent', 'update', { ...GA4_CONSENT_DENIED })
}

export function grantAnalyticsConsent(): void {
  gtag('consent', 'update', { ...GA4_CONSENT_ANALYTICS_ONLY })
}

export function disableGa4AndClearCookies(): void {
  const id = activeMeasurementId
  if (id) setGa4Disabled(id, true)
  denyGa4Consent()
  clearGa4Cookies()
}

function applySanitizedPageContext(origin: string, pathname: string, referrer: string): Record<string, string | boolean> {
  const params = buildSanitizedHitParams(origin, pathname, referrer)
  gtag('set', {
    ...params,
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  })
  return params
}

export function ensureGtagLoaded(measurementId: string, origin: string, pathname: string, referrer: string): void {
  const id = parseGa4MeasurementId(measurementId)
  if (!id) return
  setActiveGa4MeasurementId(id)
  setGa4Disabled(id, false)
  const w = window as Window & { gtag?: GtagFn }
  if (typeof w.gtag !== 'function') {
    w.gtag = gtag
  }
  const params = applySanitizedPageContext(origin, pathname, referrer)
  if (gtagConfigured) {
    grantAnalyticsConsent()
    return
  }
  gtag('consent', 'default', { ...GA4_CONSENT_DENIED })
  grantAnalyticsConsent()
  gtag('js', new Date())
  gtag('config', id, {
    ...params,
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  })
  if (!document.getElementById(GTAG_SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = GTAG_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
    document.head.appendChild(script)
  }
  gtagConfigured = true
}

export function sendPublicPageView(measurementId: string, origin: string, pathname: string, referrer: string): void {
  const id = parseGa4MeasurementId(measurementId)
  if (!id) return
  const params = applySanitizedPageContext(origin, pathname, referrer)
  gtag('event', 'page_view', {
    ...params,
    send_to: id,
  })
}
